import api from '@/api'
import store from './store'

// regex pattern of urls to ignore when showing the progress bar
const progressBarIgnorePattern = /account\/get|healthcheck\/version|identity\/token|dashboard\//

export default {
    apiRequestStarted({ commit }, config) {
        if (!config.silent && !progressBarIgnorePattern.test(config.url)) {
            commit('requestStarting')
        }
        return config
    },
    apiRequestError({ commit }, config) {
        if (!config.silent && !progressBarIgnorePattern.test(config.url)) {
            commit('requestFailed')
        }
        return Promise.reject(config)
    },
    apiResponseReceived({ commit }, response) {
        const silent = !!(response.config && response.config.silent)
        if (!silent && !progressBarIgnorePattern.test(response.request.responseURL)) {
            commit('requestCompleted')
        }
        return response
    },
    apiResponseError({ commit, dispatch }, err) {
        const silent = !!(err && err.config && err.config.silent)
        if (!silent && (!err.request || !progressBarIgnorePattern.test(err.request.responseURL))) {
            commit('requestFailed')
        }
        if (silent) return Promise.reject(err)
        // TODO: normalise error messages better here and handle more status codes

        let errorMessage = ''
        if (err && err.response) {
            switch (err.response.status) {
                case 400:
                case 422:
                    errorMessage = 'Request failed:'
                    if (err.response.data && typeof err.response.data === 'string') {
                        errorMessage += ' ' + err.response.data
                    }
                    if (err.response.data && err.response.data.errors) {
                        err.response.data.errors.forEach(function(error) {
                            if (typeof error === 'string') {
                                errorMessage += ' ' + error
                            } else {
                                errorMessage += ' ' + error.name + ': ' + error.messages.join('. ')
                            }
                        })
                    }
                    break
                case 401:
                    dispatch('gotoLogin')
                    return Promise.reject(err)
                case 403:
                    commit('showSnackbar', {
                        type: 'error',
                        text: 'You do not have the required permissions to perform this action',
                    })
                    return Promise.reject(err)
                case 404:
                    if (err.response.data && err.response.data.errors) {
                        errorMessage = err.response.data.errors.join('. ')
                    } else {
                        errorMessage = err.request.responseURL + ' not found'
                    }
                    break
                case 409:
                case 500:
                    if (err.response.data && err.response.data.error) {
                        errorMessage = err.response.data.error
                    } else if (err.response.data && err.response.data.errors) {
                        errorMessage = err.response.data.errors.join('. ')
                    } else if (err.response.data && !err.response.data.error) {
                        errorMessage = err.response.data
                    }
                    break
            }
            commit('showSnackbar', { type: 'error', text: errorMessage })
            return Promise.reject({ err, errorMessage })
        } else {
            // ignoring errors not returned by the API e.g. Network Errors etc
            dispatch('gotoLogin')
        }
    },

    checkUser({ commit }) {
        // using fetch here to bypass the axios interceptors
        fetch('/api/user/current', { method: 'GET', cache: 'no-store' }).then(response => {
            if (response.ok) {
                response.json().then(user => {
                    commit('setUser', user)
                    if (!store.events || store.events.length === 0) {
                        store.dispatch('getEvents')
                    }
                    if (!store.teams || store.teams.length === 0) {
                        store.dispatch('getTeams')
                    }
                })
            } else {
                commit('isInitialising', false)
            }
        })
    },

    getEvents({ commit }) {
        return fetch('/api/event/getCurrentList', { method: 'GET' }).then(response => {
            if (response.ok) {
                response.json().then(events => {
                    commit('setEvents', events)
                    let nextEvent = events.find(x => x.isNext)
                    commit('setNextEvent', nextEvent ? nextEvent.id : 0)
                })
            }
        })
    },

    getTeams({ commit }) {
        return fetch('/api/team/getAllActive', { method: 'GET' }).then(response => {
            if (response.ok) {
                response.json().then(teams => {
                    commit('setTeams', teams)
                })
            }
        })
    },

    gotoLogin() {
        const returnUrl =
            '?returnUrl=' +
            (window.location.pathname ? encodeURIComponent(window.location.pathname + window.location.search) : '')
        window.location.href =
            window.location.protocol + '//' + window.location.host + '/Identity/Account/LogIn' + returnUrl
    },

    async gotoLogout({ commit, dispatch }) {
        // Do NOT clear the user optimistically. We keep the UI in its authenticated
        // state (behind a blocking overlay) until the server has actually signed out,
        // so the Log In affordance is never shown while the auth cookie is still valid.
        commit('setLoggingOut', true)
        try {
            await api.logout()
            // Cookie is cleared (the awaited response carried the delete header). Hard
            // navigate to the real login form; this also tears down the SPA/store.
            window.location.href =
                window.location.protocol + '//' + window.location.host + '/Identity/Account/LogIn'
        } catch (e) {
            // Sign-out failed — the user is still logged in. Unblock and surface a single
            // error (the logout call is silent, so no global snackbar competes with this).
            commit('setLoggingOut', false)
            dispatch('showSnackbar', { type: 'error', text: 'Logout failed, please try again.' })
        }
    },

    addErrorLog(payload) {
        api.logClientError(payload)
    },

    showSnackbar({ commit }, payload) {
        commit('showSnackbar', payload)
    },
    hideSnackbar({ commit }, payload) {
        commit('hideSnackbar', payload)
    },
}
